import { useState, useMemo, useCallback, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import useNotification from "../../../../../../hooks/useNotification";

export const useVolumeManagement = ({
  bipeds,
  volumes,
  setVolumes,
  mode = "create", // 'create' ou 'edit'
}) => {
  const { showNotification } = useNotification();

  // === ESTADOS ===
  const [localVolumeName, setLocalVolumeName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedVolumeName, setSelectedVolumeName] = useState("");
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [quantities, setQuantities] = useState({});
  const [activeTab, setActiveTab] = useState("all");

  // === EFFECTS ===
  useEffect(() => {
    if (volumes && volumes.length > 0) {
      const volumeExists = volumes.some((v) => v.name === selectedVolumeName);
      if (!selectedVolumeName || !volumeExists) {
        setSelectedVolumeName(volumes[0].name);
      }
    } else {
      setSelectedVolumeName("");
    }
  }, [volumes, selectedVolumeName]);

  // === MEMOS ===
  const allocatedQuantities = useMemo(() => {
    const allocated = {};
    volumes?.forEach((volume) => {
      const items = Array.isArray(volume.items) ? volume.items : [];
      items.forEach((item) => {
        if (item.type === "product" && item.product_item_id) {
          if (!allocated[item.product_item_id]) {
            allocated[item.product_item_id] = 0;
          }
          allocated[item.product_item_id] += Number(item.quantity || 0);
        }
      });
    });
    return allocated;
  }, [volumes]);

  const allBipedItems = useMemo(() => {
    const items = Object.values(bipeds ?? {})
      .map((item) => {
        if (item.equipment) {
          const isAllocated = volumes?.some((volume) => {
            const vItems = Array.isArray(volume.items) ? volume.items : [];
            const allocated = vItems.some(
              (vItem) =>
                vItem.type === "equipment" && vItem.identify === item.identify,
            );

            return allocated;
          });

          return {
            id: item.identify,
            name: `${item.equipment.number} - ${item.equipment.name}`,
            type: "equipment",
            equipmentId: item.equipment.id || item.id,
            number: item.equipment.number,
            available: 1,
            isAllocated,
          };
        }

        if (item.product && item.service_order_item?.product) {
          const serviceOrderItem = item.service_order_item;
          const productItem = serviceOrderItem.product.items?.[0];
          const productItemId = item.id ?? productItem?.id;
          const selectedProductItem =
            serviceOrderItem.product.items?.find(
              (pi) => String(pi.id) === String(productItemId),
            ) ?? productItem;
          const productItems = (serviceOrderItem.product.items ?? []).filter(
            (pi) => Number(pi.quantity) > 0 && !pi.deleted_at,
          );
          const stockQuantity = productItems.reduce(
            (total, productItem) => total + (Number(productItem.quantity) || 0),
            0,
          );

          const category = selectedProductItem?.category?.name || "N/A";
          const condition = selectedProductItem?.condition?.name || "N/A";
          const fulfilled =
            Number(serviceOrderItem.fulfilled_quantity_product) || 0;
          const allocatableQuantity = Math.min(fulfilled, stockQuantity);

          // Verificar se ESTE item específico está alocado
          const isItemAllocated = volumes?.some((volume) => {
            const vItems = Array.isArray(volume.items) ? volume.items : [];
            return vItems.some(
              (vItem) =>
                vItem.type === "product" && vItem.identify === item.identify,
            );
          });

          // Calcular quantidade total alocada (para display e cálculos)
          const alreadyAllocated = productItems.reduce(
            (total, productItem) =>
              total + (allocatedQuantities[productItem.id] || 0),
            0,
          );
          const remaining = Math.max(0, allocatableQuantity - alreadyAllocated);
          const isFullyAllocated = remaining <= 0;

          return {
            id: item.identify,
            name: `${item.product.name} | ${category} | ${condition}`,
            displayName: item.product.name,
            code: item.product.code || selectedProductItem?.code || "",
            type: "product",
            productId:
              item.product_id || serviceOrderItem.product_id || item.id,
            productItemId,
            productItemIds: productItems.map((productItem) => productItem.id),
            available: allocatableQuantity,
            alreadyAllocated,
            remaining,
            isFullyAllocated,
            isAllocated: isItemAllocated,
            category,
            condition,
          };
        }

        return null;
      })
      .filter(Boolean);

    return items;
  }, [bipeds, allocatedQuantities, volumes]);

  // Calcula se há itens não alocados (para modo edit)
  const hasUnallocatedItems = useMemo(() => {
    if (mode !== "edit") return false;

    return allBipedItems.some((item) => {
      if (item.type === "equipment") {
        return !item.isAllocated;
      }
      if (item.type === "product") {
        return item.remaining > 0 && !item.isAllocated;
      }
      return false;
    });
  }, [allBipedItems, mode]);

  const filteredItems = useMemo(() => {
    return allBipedItems.filter((item) => {
      const matchesSearch =
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(item.id).toLowerCase().includes(searchTerm.toLowerCase());

      const matchesTab =
        activeTab === "all" ||
        (activeTab === "equipment" && item.type === "equipment") ||
        (activeTab === "product" && item.type === "product");

      return matchesSearch && matchesTab;
    });
  }, [allBipedItems, searchTerm, activeTab]);

  const selectableItemsOnScreen = useMemo(() => {
    return filteredItems.filter((item) =>
      item.type === "equipment"
        ? !item.isAllocated
        : !item.isFullyAllocated && !item.isAllocated,
    );
  }, [filteredItems]);

  const allSelected = useMemo(() => {
    if (selectableItemsOnScreen.length === 0) return false;
    return selectableItemsOnScreen.every((item) => selectedItems.has(item.id));
  }, [selectableItemsOnScreen, selectedItems]);

  // === CALLBACKS ===
  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedItems((prev) => {
        const next = new Set(prev);
        selectableItemsOnScreen.forEach((item) => next.delete(item.id));
        return next;
      });
    } else {
      setSelectedItems((prev) => {
        const next = new Set(prev);
        selectableItemsOnScreen.forEach((item) => next.add(item.id));
        return next;
      });
    }
  }, [allSelected, selectableItemsOnScreen]);

  const handleToggleSelectItem = useCallback((id) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleQuantityChange = useCallback((id, value) => {
    setQuantities((prev) => ({ ...prev, [id]: value }));
  }, []);

  const handleCreateVolume = () => {
    const trimmed = localVolumeName.trim();
    if (!trimmed) {
      showNotification("warning", "Por favor, digite um nome para o volume");
      return;
    }

    const exists = volumes.some(
      (v) => v.name.toLowerCase() === trimmed.toLowerCase(),
    );
    if (exists) {
      showNotification("warning", "Já existe um volume com este nome");
      return;
    }

    const newVolObj = {
      identifier: uuidv4(),
      name: trimmed,
      items: [],
    };

    setVolumes((prev) => [...prev, newVolObj]);
    setSelectedVolumeName(trimmed);
    setLocalVolumeName("");
    showNotification("success", `Volume "${trimmed}" criado com sucesso!`);
  };

  const handleAddSingleItem = useCallback(
    (item) => {
      if (!selectedVolumeName) {
        showNotification("warning", "Selecione ou crie um volume primeiro");
        return;
      }

      // Impede adicionar equipamento sem equipment_id
      if (item.type === "equipment" && !item.equipmentId) {
        showNotification(
          "warning",
          "Aguarde o bip do equipamento completar antes de adicionar ao volume.",
        );
        return;
      }

      // Impede adicionar produto sem productItemId
      if (item.type === "product" && !item.productItemId) {
        showNotification(
          "warning",
          "Aguarde o bip do insumo completar antes de adicionar ao volume.",
        );
        return;
      }

      const rawInput = quantities[item.id];
      let qty = 0;

      if (item.type === "product") {
        qty = rawInput ? parseInt(rawInput, 10) : item.remaining;
        if (isNaN(qty) || qty <= 0) qty = item.remaining;
      } else {
        qty = 1;
      }

      if (item.type === "product" && qty > item.remaining) {
        showNotification(
          "warning",
          `Quantidade excedida para ${item.displayName}. Restam apenas ${item.remaining} unidades.`,
        );
        return;
      }

      setVolumes((prevVolumes) => {
        return prevVolumes.map((volume) => {
          if (volume.name !== selectedVolumeName) return volume;

          const newItems = Array.isArray(volume.items) ? [...volume.items] : [];

          if (item.type === "equipment") {
            const exists = newItems.some((i) => i.identify === item.id);
            if (!exists) {
              newItems.push({
                equipment_id: item.equipmentId,
                equipment_number: item.number,
                equipment_name: item.name,
                type: "equipment",
                identify: item.id,
              });
            }
          } else if (item.type === "product") {
            const existingIndex = newItems.findIndex(
              (i) => i.product_item_id === item.productItemId,
            );

            if (existingIndex !== -1) {
              const currentQty =
                parseInt(newItems[existingIndex].quantity, 10) || 0;
              const targetQty = currentQty + qty;

              const otherVolumesAllocated =
                item.productItemIds.reduce(
                  (total, id) => total + (allocatedQuantities[id] || 0),
                  0,
                ) - currentQty;
              const maxAllowedInThisVolume =
                item.available - otherVolumesAllocated;

              newItems[existingIndex].quantity = Math.min(
                targetQty,
                maxAllowedInThisVolume,
              );
            } else {
              newItems.push({
                product_id: item.productId,
                product_item_id: item.productItemId,
                product_code: item.code,
                product_name: item.displayName,
                product_category: item.category,
                product_condition: item.condition,
                quantity: qty,
                type: "product",
                identify: item.id,
              });
            }
          }

          return { ...volume, items: newItems };
        });
      });

      setQuantities((prev) => ({ ...prev, [item.id]: "" }));
      showNotification("success", "Item adicionado ao volume!");
    },
    [
      selectedVolumeName,
      quantities,
      setVolumes,
      showNotification,
      allocatedQuantities,
    ],
  );

  const handleAddSelectedToVolume = useCallback(() => {
    if (!selectedVolumeName) {
      showNotification("warning", "Selecione ou crie um volume primeiro");
      return;
    }
    if (selectedItems.size === 0) {
      showNotification("warning", "Nenhum item selecionado na lista");
      return;
    }

    const itemsToAdd = filteredItems.filter((item) =>
      selectedItems.has(item.id),
    );
    let validItemsCount = 0;

    setVolumes((prevVolumes) => {
      return prevVolumes.map((volume) => {
        if (volume.name !== selectedVolumeName) return volume;

        const newItems = Array.isArray(volume.items) ? [...volume.items] : [];

        itemsToAdd.forEach((item) => {
          if (item.type === "equipment") {
            const exists = newItems.some((i) => i.identify === item.id);
            if (!exists) {
              newItems.push({
                equipment_id: item.equipmentId,
                equipment_number: item.number,
                equipment_name: item.name,
                type: "equipment",
                identify: item.id,
              });
              validItemsCount++;
            }
          } else if (item.type === "product") {
            const rawInput = quantities[item.id];
            let qty = rawInput ? parseInt(rawInput, 10) : item.remaining;
            if (isNaN(qty) || qty <= 0) qty = item.remaining;

            if (qty > item.remaining) return;

            const existingIndex = newItems.findIndex(
              (i) => i.product_item_id === item.productItemId,
            );

            if (existingIndex !== -1) {
              const currentQty =
                parseInt(newItems[existingIndex].quantity, 10) || 0;
              const targetQty = currentQty + qty;
              const otherVolumesAllocated =
                item.productItemIds.reduce(
                  (total, id) => total + (allocatedQuantities[id] || 0),
                  0,
                ) - currentQty;
              const maxAllowedInThisVolume =
                item.available - otherVolumesAllocated;

              newItems[existingIndex].quantity = Math.min(
                targetQty,
                maxAllowedInThisVolume,
              );
              validItemsCount++;
            } else {
              newItems.push({
                product_id: item.productId,
                product_item_id: item.productItemId,
                product_code: item.code,
                product_name: item.displayName,
                product_category: item.category,
                product_condition: item.condition,
                quantity: qty,
                type: "product",
                identify: item.id,
              });
              validItemsCount++;
            }
          }
        });

        return { ...volume, items: newItems };
      });
    });

    setSelectedItems(new Set());
    setQuantities((prev) => {
      const updated = { ...prev };
      itemsToAdd.forEach((item) => {
        updated[item.id] = "";
      });
      return updated;
    });

    if (validItemsCount > 0) {
      showNotification("success", "Itens adicionados com sucesso ao volume!");
    }
  }, [
    selectedVolumeName,
    selectedItems,
    filteredItems,
    quantities,
    setVolumes,
    showNotification,
    allocatedQuantities,
  ]);

  const handleQuantityKeyDown = useCallback(
    (e, item) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleAddSingleItem(item);
      }
    },
    [handleAddSingleItem],
  );

  // === RETURN ===
  return {
    // Estados
    localVolumeName,
    setLocalVolumeName,
    searchTerm,
    setSearchTerm,
    selectedVolumeName,
    setSelectedVolumeName,
    selectedItems,
    setSelectedItems,
    quantities,
    setQuantities,
    activeTab,
    setActiveTab,

    // Memos
    allocatedQuantities,
    allBipedItems,
    filteredItems,
    selectableItemsOnScreen,
    allSelected,
    hasUnallocatedItems,

    // Callbacks e Funções
    handleSelectAll,
    handleToggleSelectItem,
    handleQuantityChange,
    handleCreateVolume,
    handleAddSingleItem,
    handleAddSelectedToVolume,
    handleQuantityKeyDown,

    // Modo
    mode,
  };
};
